import React, {
  Suspense,
  useState,
  useEffect,
  ReactElement,
  ReactText,
} from "react";
import {
  Flex,
  BoxProps,
  Box,
  useColorModeValue,
  Text,
  CloseButton,
  FlexProps,
  Link,
  Drawer,
  DrawerContent,
  useDisclosure,
  IconButton,
  HStack,
  Menu,
  MenuButton,
  Avatar,
  MenuList,
  MenuItem,
  MenuDivider,
  useToast,
} from "@chakra-ui/react";
import { Redirect } from "react-router";
import {
  useLocation,
  useHistory,
  NavLink,
  useRouteMatch,
} from "react-router-dom";
import LoadingOverlay from "components/LoadingOverlay";
import { selectAuth, logout } from "redux/slices/authSlice";
import { useTypedSelector, useAppDispatch } from "redux/hooks";
import {
  useGetProfileLazyQuery,
  useGetProfileQueryState,
  useGetProfileQuery,
  useCreateUserMutation,
} from "redux/services/membership";
import { FiMenu, FiChevronDown } from "react-icons/fi";
import { RoutesLevel2, Roles, RoleOptions } from "common";
import { isPermitted } from "utils";
import Image from "components/Image";
import logo from "assets/images/tyme-logo.png";
import { useLogoutMutation } from "redux/services/auth";
import CreateEnterpriseUser from "containers/CreateEnterpriseUser";
import InviteOperationUser from "containers/InviteOperationUser";
import ChangePasswordModal from "containers/ChangePasswordModal";
import { GetProfileResponse } from "redux/services/membership/types";
import env from "env";
import { clearError } from "redux/slices/errorSlice";

interface NavItemProps extends FlexProps {
  icon: ReactElement;
  children: ReactText;
  link: string;
}

interface Props {
  children: React.ReactNode;
  forceAccessToken?: boolean;
}

interface SidebarProps extends BoxProps {
  permittedRoute: {
    label: string;
    icon: React.ReactElement;
    link: string;
    roles: string[];
    isActive?: boolean;
  }[];
  profileData?: GetProfileResponse;
  onClose: () => void;
}

interface MobileProps extends FlexProps {
  onOpen: () => void;
}

const NavItem = ({ icon, link, children, ...rest }: NavItemProps) => {
  const location = useLocation();

  return (
    <Link
      to={link}
      as={NavLink as any}
      style={{ textDecoration: "none" }}
      _focus={{ boxShadow: "none" }}
      _active={{
        "&>Flex": { color: "red !important" },
      }}
    >
      <Flex
        align="center"
        p="15px 15px 15px 30px"
        role="group"
        cursor="pointer"
        color="#000"
        bg={
          link.split("?")[0] === location.pathname
            ? "linear-gradient(90deg, #e6f4fe 0%, #e6f4fe 100%)"
            : "transparent"
        }
        _hover={{
          bg: "linear-gradient(90deg, #e6f4fe 0%, #e6f4fe 100%);",
          color: "white",
        }}
        {...rest}
      >
        <Box
          flex="0 0 25px"
          width="25px"
          height="25px"
          maxWidth="25px"
          transform="translateY(-2px)"
        >
          {icon}
        </Box>
        <Text ml="10px" color="#000" lineHeight="1.5">
          {children}
        </Text>
      </Flex>
    </Link>
  );
};

const SidebarContent = ({
  onClose,
  permittedRoute,
  profileData,
  ...rest
}: SidebarProps) => {
  return (
    <Box
      transition="3s ease"
      bg={`linear-gradient(to right, #fff 0%, #fff 100%)`}
      borderRight="1px"
      borderRightColor={useColorModeValue("gray.200", "gray.700")}
      w={{ base: "full", md: 60 }}
      pos="fixed"
      h="full"
      {...rest}
    >
      <Flex h="20" alignItems="center" mx="8" justifyContent="space-between">
        <Image src={logo} w="128px" alt="gotyme-bank-logo" />
        <CloseButton display={{ base: "flex", md: "none" }} onClick={onClose} />
      </Flex>
      {permittedRoute
        .filter((link) => link.isActive)
        .map((link) => {
          const role = RoleOptions.find((option) =>
            profileData?.data?.listRoles?.includes(option.value)
          );

          let finalLink = link.link;

          if (finalLink === "/audit-activity") {
            switch (role?.value) {
              case Roles.BusinessApprover:
                finalLink = `${link.link}?executedBy=${profileData?.data.userId}`;
                break;
              case Roles.BusinessOwner:
                finalLink = `${link.link}?resourceOwnerId=${profileData?.data.userId}`;
                break;
              default:
                finalLink = link.link;
                break;
            }
          }

          return (
            <NavItem key={link.label} icon={link.icon} link={finalLink}>
              {link.label}
            </NavItem>
          );
        })}
    </Box>
  );
};

const AuthLayout = ({ children, forceAccessToken }: Props) => {
  const match = useRouteMatch();
  const { accessToken } = useTypedSelector(selectAuth);
  const { data } = useGetProfileQueryState();
  const { data: profileData } = useGetProfileQuery();
  const [trigger, { isSuccess, isError, error }] = useGetProfileLazyQuery();
  const [waitRequiredRequests, setWaitRequiredRequests] = useState(!data);
  const location = useLocation();
  const history = useHistory();
  const toast = useToast();
  const [routeData, setRouteData] = useState<any>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [createUser, { isSuccess: isCreateUserSuccess }] =
    useCreateUserMutation();

  const permittedRoute = () => {
    return RoutesLevel2.map((routeItem) => {
      if (routeItem?.roles?.length === 0) {
        return { ...routeItem, isActive: true };
      }

      return {
        ...routeItem,
        isActive: profileData?.data?.listRoles.some(
          (r) => routeItem?.roles.indexOf(r) >= 0
        ),
      };
    });
  };

  useEffect(() => {
    if (isError && (error as any).status === 404 && accessToken) {
      const parseToken = accessToken.split(".")[1];
      const jsonToken = JSON.parse(atob(parseToken));

      if (jsonToken.roles) {
        const isRolesIsString = typeof jsonToken.roles === "string";

        const isPaymentOpsUser = isRolesIsString
          ? jsonToken.roles === env.app.paymentOptsRole
          : jsonToken.roles.includes(env.app.paymentOptsRole);

        if (isPaymentOpsUser) {
          createUser({
            emailAddress: jsonToken.email,
            firstName: jsonToken.given_name,
            lastName: jsonToken.family_name,
            userId: jsonToken.externalidpid,
            listRoles: isRolesIsString ? [jsonToken.roles] : jsonToken.roles,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, error, accessToken]);

  useEffect(() => {
    if (waitRequiredRequests || isCreateUserSuccess) {
      trigger();
    }
  }, [waitRequiredRequests, isCreateUserSuccess, trigger]);

  useEffect(() => {
    if (!waitRequiredRequests) {
      const route = RoutesLevel2.find((n) => n.link === match.path);
      // check if the url entered is permitted for the user.(prevent manual url entering)
      setRouteData(route);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location,
    waitRequiredRequests,
    history,
    data?.data.listRoles,
    profileData,
  ]);

  useEffect(() => {
    if (isSuccess) setWaitRequiredRequests(false);
  }, [isSuccess]);

  useEffect(() => {
    if (profileData && routeData) {
      const isPermittedRoute = isPermitted(
        profileData?.data?.listRoles || [],
        routeData?.roles ?? []
      );
      if (!isPermittedRoute) {
        history.push("/");
        toast({
          title: "Access denied: Insufficient permissions for this screen",
          description: "",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData, routeData]);

  if (!accessToken && !forceAccessToken)
    return (
      <Redirect to={{ pathname: "/", state: { from: location.pathname } }} />
    );

  return waitRequiredRequests && !forceAccessToken ? (
    <LoadingOverlay h="100%" />
  ) : (
    <Box minH="100vh" position="relative">
      <SidebarContent
        profileData={profileData}
        permittedRoute={permittedRoute()}
        onClose={onClose}
        display={{ base: "none", md: "block" }}
      />
      <Drawer
        autoFocus={false}
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        returnFocusOnClose={false}
        onOverlayClick={onClose}
        size="full"
      >
        <DrawerContent>
          <SidebarContent
            profileData={profileData}
            permittedRoute={permittedRoute()}
            onClose={onClose}
          />
        </DrawerContent>
      </Drawer>

      <MobileNav onOpen={onOpen} />

      <Box ml={{ base: 0, md: 60 }} height="calc(100vh - 80px)">
        <Box>
          <Box display="block" p={"20px"} position={"relative"}>
            <Flex
              borderRadius="10px 10px 10px 10px"
              bg="white"
              w="100%"
              borderWidth="1px"
            >
              <Suspense fallback={<LoadingOverlay />}>{children}</Suspense>
            </Flex>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const MobileNav = ({ onOpen, ...rest }: MobileProps) => {
  const dispatch = useAppDispatch();
  const { data: profileData } = useGetProfileQuery();
  const { accessToken } = useTypedSelector(selectAuth);
  const [logoutServer] = useLogoutMutation();

  const onLogout = () => {
    dispatch(logout());
    logoutServer(accessToken as string);
    window.location.href = `${env.api.baseUrl.tokenBaseUrl}oidc/logout?client_id=${env.app.authCodeAppId}&post_logout_redirect_uri=${env.app.rootUrl}`;
  };

  const isPaymentOpsRole =
    profileData?.data?.listRoles &&
    profileData?.data?.listRoles.indexOf(Roles.PaymentOps) !== -1;

  const isBusinessAdmin = profileData?.data?.listRoles?.includes(
    Roles.BusinessAdmin
  );

  const [openModalCreate, setOpenModalCreate] = useState(false);
  const [openModalInviteOpts, setOpenModalInviteOpts] = useState(false);
  const [openModalChangePassword, setOpenModalChangePassword] = useState(false);

  return (
    <Flex
      ml={{ base: 0, md: 60 }}
      px={{ base: 4, md: 4 }}
      height="20"
      alignItems="center"
      bg={useColorModeValue("white", "gray.900")}
      borderBottomColor={useColorModeValue("gray.200", "gray.700")}
      justifyContent={{ base: "space-between", md: "flex-end" }}
      {...rest}
    >
      <IconButton
        display={{ base: "flex", md: "none" }}
        onClick={onOpen}
        variant="outline"
        aria-label="open menu"
        icon={<FiMenu />}
      />
      <HStack
        spacing={{ base: "0", md: "12" }}
        w="100%"
        justifyContent="space-between"
      >
        <Flex alignItems="center" ml={["20px", "20px", "unset"]} />

        <Flex alignItems={"center"}>
          <Menu placement="bottom-end">
            <HStack>
              <Box ml="2">
                <Text fontSize="sm">
                  {profileData?.data?.firstName} {profileData?.data?.lastName} (
                  {RoleOptions.find((option) =>
                    profileData?.data?.listRoles?.includes(option.value)
                  )?.label || "Enterprise User"}
                  )
                </Text>
                {!isPaymentOpsRole && (
                  <Text
                    fontSize={12}
                    textAlign="right"
                    color="blue.500"
                    mt={1}
                    cursor="pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open("/payroll-disbursement-user-guide/index.html", "_blank");
                    }}
                  >
                    Payroll Disbursement User Guide
                  </Text>
                )}
              </Box>
              <MenuButton
                py={2}
                transition="all 0.3s"
                _focus={{ boxShadow: "none" }}
              >
                <HStack>
                  <Avatar
                    size="sm"
                    bg="#EBEBEB"
                    name={`${profileData?.data?.firstName} ${profileData?.data?.lastName}`}
                  />
                  <Box display={{ base: "none", md: "flex" }}>
                    <FiChevronDown />
                  </Box>
                </HStack>
              </MenuButton>
            </HStack>
            <MenuList
              bg={useColorModeValue("white", "gray.900")}
              borderColor={useColorModeValue("gray.200", "gray.700")}
            >
              {(isPaymentOpsRole || isBusinessAdmin) && (
                <>
                  <MenuItem
                    onClick={() => {
                      setOpenModalCreate(true);
                      dispatch(clearError());
                    }}
                  >
                    Register {isPaymentOpsRole ? "Business Administrator" : "Enterprise User"}
                  </MenuItem>
                  <MenuDivider />
                </>
              )}

              {!isPaymentOpsRole && (
                <MenuItem
                  onClick={() => {
                    setOpenModalChangePassword(true);
                  }}
                >
                  Change Password
                </MenuItem>
              )}

              <MenuItem
                onClick={() => {
                  onLogout();
                }}
              >
                Sign out
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </HStack>

      {openModalCreate && (
        <CreateEnterpriseUser
          isOpen={openModalCreate}
          setOpenModalCreate={() => setOpenModalCreate(false)}
        />
      )}

      {openModalInviteOpts && (
        <InviteOperationUser
          isOpen={openModalInviteOpts}
          setOpenModalCreate={() => setOpenModalInviteOpts(false)}
        />
      )}

      <ChangePasswordModal
        visible={openModalChangePassword}
        onClose={() => setOpenModalChangePassword(false)}
      />
    </Flex>
  );
};

export default AuthLayout;
